/**
 * Hook для работы с аутентификацией и правами доступа
 */

import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import type { User } from '../types';

export interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  logout: () => void;
}

/**
 * Hook для доступа к auth данным и проверки прав
 */
export function useAuth(): UseAuthReturn {
  const { user, isAuthenticated, logout } = useAuthStore();

  /**
   * Проверить, есть ли у пользователя указанное разрешение
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user || !Array.isArray(user.permissions) || !Array.isArray(user.roles)) {
        return false;
      }

      // Администратор имеет все права
      if (user.roles.includes('admin') || user.roles.includes('administrator')) {
        return true;
      }

      // Проверка конкретного разрешения
      return user.permissions.includes(permission);
    },
    [user]
  );

  /**
   * Проверить, есть ли у пользователя хотя бы одно из указанных разрешений
   */
  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      return permissions.some((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  /**
   * Проверить, есть ли у пользователя все указанные разрешения
   */
  const hasAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      return permissions.every((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  return {
    user,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    logout,
  };
}

